import { useEffect, useState } from "react";
import { MapPin, Phone, Store, Link, Link2Off, Play } from "lucide-react";
import Swal from "sweetalert2";
import { ConnectModal } from "../components/ConnectModal";
import { TestModal } from "../components/TestModal";
import { disconnectMokaOutlet } from "../services/moka";

interface MokaOutletItem {
  TenantId: number;
  OutletId: number;
  OutletName: string;
  Address: string;
  PhoneNumber: string | null;
  City: string | null;
  Province: string | null;
  PostalCode: string | null;
  IsConnected: boolean;
}

export default function ThanksPage() {
  const code = sessionStorage.getItem("moka_code") ?? "";
  const [outlets, setOutlets] = useState<MokaOutletItem[]>([]);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("moka_outlets");
      if (raw) setOutlets(JSON.parse(raw));
    } catch {
      Swal.fire({
        title: "Error",
        text: "Failed to load outlet data.",
        icon: "error",
      });
    }
  }, []);

  const [connectModalOpen, setConnectModalOpen] = useState<boolean>(false);
  const [selectedOutlet, setSelectedOutlet] = useState<MokaOutletItem | null>(
    null,
  );

  const [testModalOpen, setTestModalOpen] = useState<boolean>(false);
  const [selectedOutletForTest, setSelectedOutletForTest] =
    useState<MokaOutletItem | null>(null);

  const handleConnect = (outlet: MokaOutletItem) => {
    setSelectedOutlet(outlet);
    setConnectModalOpen(true);
  };

  const handleOpenTestModal = (outlet: MokaOutletItem) => {
    setSelectedOutletForTest(outlet);
    setTestModalOpen(true);
  };

  const handleDisconnect = async (outletId: number) => {
    try {
      await disconnectMokaOutlet(outletId);

      await Swal.fire({
        title: "Disconnected",
        text: "Outlet has been disconnected successfully.",
        icon: "success",
        showConfirmButton: false,
        timer: 1500,
        timerProgressBar: true,
        allowOutsideClick: false,
      });

      // Update local state so the badge & button reflect the change
      setOutlets((prev) =>
        prev.map((o) =>
          o.OutletId === outletId ? { ...o, IsConnected: false } : o,
        ),
      );
    } catch (err: any) {
      const message =
        err.response?.data?.Message ||
        err.response?.data?.message ||
        "Failed to disconnect outlet.";
      await Swal.fire({
        title: "Error",
        text: message,
        icon: "error",
      });
    }
  };

  const handleConnected = (tenantId: number) => {
    if (selectedOutlet === null) return;
    setOutlets((prev) =>
      prev.map((o) =>
        o.OutletId === selectedOutlet.OutletId
          ? { ...o, IsConnected: true, TenantId: tenantId }
          : o,
      ),
    );
    setConnectModalOpen(false);
    setSelectedOutlet(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white p-4 sm:p-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <svg
              className="w-8 h-8 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
            Login Successful
          </h1>
          <p className="text-gray-500 text-sm sm:text-base">
            Here are your registered Moka outlets
          </p>
        </div>

        {/* Outlet list */}
        {outlets.length === 0 ? (
          <div className="bg-white rounded-2xl shadow p-10 text-center text-gray-400">
            <Store className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p>No outlet data found.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {outlets.map((outlet) => (
              <div
                key={outlet.OutletId}
                className="bg-white rounded-2xl shadow hover:shadow-md transition-shadow p-5 flex flex-col gap-3 border border-gray-100"
              >
                {/* Outlet name + sync badge */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="mt-0.5 flex-shrink-0 w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Store className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-gray-400 font-medium">
                        Outlet
                      </p>
                      <p className="text-sm font-semibold text-gray-800 leading-tight break-words">
                        {outlet.OutletName}
                      </p>
                    </div>
                  </div>

                  {/* Sync / Not Sync badge */}
                  {outlet.IsConnected ? (
                    <span className="flex-shrink-0 inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                      Sync
                    </span>
                  ) : (
                    <span className="flex-shrink-0 inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-400 inline-block" />
                      Not Sync
                    </span>
                  )}
                </div>

                {/* Address */}
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex-shrink-0 w-9 h-9 bg-indigo-50 rounded-lg flex items-center justify-center">
                    <MapPin className="w-4 h-4 text-indigo-500" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 font-medium">Address</p>
                    <p className="text-sm text-gray-600 leading-snug">
                      {outlet.Address || "-"}
                    </p>
                  </div>
                </div>

                {/* Phone (only if available) */}
                {outlet.PhoneNumber && (
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex-shrink-0 w-9 h-9 bg-green-50 rounded-lg flex items-center justify-center">
                      <Phone className="w-4 h-4 text-green-500" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium">Phone</p>
                      <p className="text-sm text-gray-600">
                        {outlet.PhoneNumber}
                      </p>
                    </div>
                  </div>
                )}

                {/* Connect / Disconnect buttons */}
                <div className="mt-auto pt-3 border-t border-gray-100 flex flex-col gap-2">
                  <button
                    onClick={() =>
                      outlet.IsConnected
                        ? handleDisconnect(outlet.OutletId)
                        : handleConnect(outlet)
                    }
                    className={`inline-flex items-center justify-center gap-1.5 text-xs font-semibold py-2 px-3 rounded-lg transition-all text-white ${outlet.IsConnected ? "bg-green-600 hover:bg-green-700" : "bg-blue-600  hover:bg-blue-700"}`}
                  >
                    {outlet.IsConnected ? (
                      <Link className="w-3.5 h-3.5" />
                    ) : (
                      <Link2Off className="w-3.5 h-3.5" />
                    )}
                    {outlet.IsConnected ? "Disconnect" : "Connect"}
                  </button>
                  {outlet.IsConnected && (
                    <button
                      onClick={() => handleOpenTestModal(outlet)}
                      className="inline-flex items-center justify-center gap-2 text-xs font-bold py-2 px-3 rounded-lg transition-all bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-sm border border-orange-400/20 active:scale-95"
                    >
                      <Play className="w-3.5 h-3.5 fill-current" />
                      Test
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Connect Modal */}
      {selectedOutlet !== null && (
        <ConnectModal
          outlet={selectedOutlet}
          isOpen={connectModalOpen}
          onClose={() => {
            setConnectModalOpen(false);
            setSelectedOutlet(null);
          }}
          onConnected={handleConnected}
          code={code ?? ""}
        />
      )}

      {/* Test Modal */}
      {selectedOutletForTest !== null && (
        <TestModal
          outlet={selectedOutletForTest}
          isOpen={testModalOpen}
          onClose={() => {
            setTestModalOpen(false);
            setSelectedOutletForTest(null);
          }}
        />
      )}
    </div>
  );
}
